import React, { useCallback, useMemo, useState } from 'react';
import s from './s.module.less';
import { Button, Modal, message } from 'antd';
import { IEducation, EditModuleEnum, FormFields, IProviderStore } from 'types/practiceFront';
import dayjs from 'utils/dayjs';
import ProviderStore from 'store/PracticeFront/provider';
import { removeEducation } from 'api/practiceFront';
import EditStatusStore from 'store/PracticeFront/editStatus';
import remove from 'lodash/remove';
import EducationItem from './components/EducationItem';
import ErrorBlock from '../ErrorBlock';
import useRefreshPreview from 'hooks/useRefreshPreview';

interface IProps {
    readonly?: boolean;
    showEditingTip?: boolean;
    initFormValue: Array<IEducation>;
    onSubmit: (value: IEducation, field: EditModuleEnum, callback: () => void) => void;
    onEditStatusChange: (status: boolean, field: EditModuleEnum) => void;
    refetch: () => void;
    scrollToTheEditingModule?: () => void;
    setShowEditingTip?: (status: boolean) => void;
}

const Education = ({
    readonly,
    showEditingTip,
    initFormValue,
    onSubmit,
    onEditStatusChange,
    refetch,
    scrollToTheEditingModule,
    setShowEditingTip,
}: IProps) => {
    const [getProvider, setProvider] = ProviderStore.useStore();
    const [getEditStatus] = EditStatusStore.useStore();
    const editStatusMap = getEditStatus('data');
    const gloablEditStatus = useMemo(() => !editStatusMap || !!Object.values(editStatusMap).find((e) => e), [editStatusMap]);
    const [, contextHolder] = message.useMessage();
    const [currentEditingIndex, setCurrentEditingIndex] = useState(-1);
    const [currentId, setCurrentId] = useState<number | undefined>();
    const [currentRemoveIndex, setCurrentRemoveIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    const [setPreview] = useRefreshPreview();

    const providerData = getProvider('data') as IProviderStore;

    const handleEditStatusChange = useCallback((status: boolean, index: number) => {
        if (gloablEditStatus && status) {
            scrollToTheEditingModule?.();
            setShowEditingTip?.(true);
            message.error('Please save your changes before proceeding');
        } else {
            setShowEditingTip?.(false);
            setCurrentEditingIndex(status ? index : -1);
            onEditStatusChange(status, EditModuleEnum.EDUCATION);
        }
    }, [gloablEditStatus, onEditStatusChange, scrollToTheEditingModule, setShowEditingTip]);

    const submit = useCallback(async (values: IEducation, id?: number) => {
        onSubmit({
            ...values,
            endDate: values.currently ? dayjs(undefined) : values.endDate,
            id,
        }, EditModuleEnum.EDUCATION, () => setCurrentEditingIndex(-1));
    }, [onSubmit]);

    const handleShowModal = useCallback((index: number, id?: number) => {
        if (gloablEditStatus) {
            scrollToTheEditingModule?.();
            setShowEditingTip?.(true);
            message.error('Please save your changes before proceeding');
        } else {
            setShowEditingTip?.(false);
            setCurrentId(id);
            setCurrentRemoveIndex(index);
            setOpen(true);
        }
    }, [gloablEditStatus, scrollToTheEditingModule, setShowEditingTip]);

    const handleHideModal = useCallback(() => {
        setCurrentId(undefined);
        setOpen(false);
    }, []);

    const handleRemove = useCallback(async () => {
        if (currentId) {
            const res = await removeEducation(currentId);

            if (res.error) {
                message.error(res.error);
            } else {
                setPreview('flag', {});
                setOpen(false);
                refetch();
            }
        } else {
            remove(initFormValue, (e, i) => i === currentRemoveIndex);
            setProvider('data', { ...providerData, education: [...initFormValue] });
            setOpen(false);
        }
    }, [currentId, setPreview, refetch, initFormValue, setProvider, providerData, currentRemoveIndex]);

    const handleAdd = useCallback(() => {
        if (gloablEditStatus) {
            scrollToTheEditingModule?.();
            setShowEditingTip?.(true);
            message.error('Please save your changes before proceeding');
        } else {
            setShowEditingTip?.(false);
            const newEducation = {
                [FormFields.SCHOOL_NAME]: '',
                [FormFields.MAJOR]: '',
                [FormFields.IS_PURSUING_DEGREE]: false,
                [FormFields.DEGREE]: '',
                [FormFields.SPECIALTIES]: '',
                [FormFields.CERTIFICATES]: '',
                [FormFields.START_DATE]: dayjs(undefined),
                [FormFields.END_DATE]: dayjs(undefined),
                [FormFields.SCHOOL_LOGO]: '',
            };
            const newStore = {
                ...providerData,
                education: [newEducation, ...(providerData.education || [])],
            };

            setProvider('data', newStore);
            setCurrentEditingIndex(0);
            onEditStatusChange(true, EditModuleEnum.EDUCATION);
        }
    }, [gloablEditStatus, onEditStatusChange, providerData, scrollToTheEditingModule, setProvider, setShowEditingTip]);

    return (
        <div className={s.wrap} id={EditModuleEnum.EDUCATION}>
            {contextHolder}
            <Modal title="Remove this information in UniProfile?" open={open} onOk={handleRemove} onCancel={handleHideModal} okText="Remove">
                <p>The information will also be removed from UniProfile. Please note that this will be applied to your UniProfile and updated across all channels. Are you sure you want to proceed?</p>
            </Modal>
            <div className={s.header}>
                <div className={s.title}>Education</div>
                {
                    !readonly &&
                    <div className={s.btns}>
                        <Button onClick={handleAdd} type="primary">Add</Button>
                    </div>
                }
            </div>
            {
                showEditingTip && currentEditingIndex !== -1 && <ErrorBlock />
            }
            <div className={s.body}>
                {
                    initFormValue && initFormValue.length > 0 ?
                        initFormValue?.map((item, index) => {
                            const editing = index === currentEditingIndex;
                            const { id } = item;

                            return (
                                <div key={id || index} className={s.item}>
                                    <EducationItem
                                        readonly={readonly}
                                        id={id}
                                        index={index}
                                        isEditting={editing}
                                        data={item}
                                        onEditStatusChange={handleEditStatusChange}
                                        onSubmit={submit}
                                        onRemove={handleShowModal}
                                    />
                                </div>
                            );
                        }) :
                        <div className={s.noncontent}>Nothing here yet</div>
                }

            </div>
        </div>
    );
};

export default Education;
